import { FormElementType, ExpenseTableColumn } from "./types"

export const getStatus = (a: number, t: (key: string) => string) => {
    switch (a) {
        case 0: return { label: t('status_unhandled'), icon: "circle-minus", className: "text-secondary" }
        case 1: return { label: t('status_assigned'), base_label: t('status_assigned_base'), icon: "circle-check", className: "text-success" }
        case 2: return { label: t('status_rejected'), base_label: t('status_rejected_base'), icon: "times-circle", className: "text-danger" }
        case 9: return { label: 'Kladd', icon: "pencil", className: 'text-warning' }

    }
}

export const formatValueWithPattern = (value: string | number, pattern: string): string => {
    const cleanValue = String(value).replace(/\D/g, ""); // Remove non-digit characters
    let formatted = "";
    let valueIndex = 0;

    for (let i = 0; i < pattern.length; i++) {
        if (pattern[i] === "0" && valueIndex < cleanValue.length) { // Check for '0' instead of 'X'
            formatted += cleanValue[valueIndex];
            valueIndex++;
        } else if (pattern[i] !== "0") {
            formatted += pattern[i]; // Add any other characters from the pattern
        }
    }

    return formatted;
};

export const formatNumber = (a: string | number | null | undefined) => {
    if (!a) return null;
    return a.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}

export const attachmentLabel = (a: string, t: (key: string) => string) => {
    switch (a) {
        case 'filer_bilag': return t('files_voucher');
        case 'filer_prosjektbeskrivelse': return t('files_project_description');
        case 'filer_regnskap': return t('files_accounting');
        case 'filer_prosjektbudsjett': return t('files_project_budget');
        case 'filer_finansieringsplan': return t('files_financial_plan');
        case 'filer_fremdriftsplan': return t('files_progress_plan');
        case 'filer_andre1': return t('files_other_1');
        case 'filer_andre2': return t('files_other_2');
        default: return t('files_other_1')
    }
}

export const fileIcon = (a: string) => {
    switch (a) {
        case 'pdf': return "file-pdf"
        case 'xls': case 'xlsx': return "file-excel"
        case 'csv': return "file-csv"
        case 'ppt': case 'pptx': return "file-powerpoint"
        case 'zip': case 'rar': return "file-zipper"
        case 'doc': case 'docx': return "file-word"
        case 'jpg': case 'png': return "file-image"
        case 'mp4': return "file-video"
        default: return "file"
    }
}

/**
 * Full URL til ikonet i tips-boksen på et skjema.
 *
 * Form-builder laster nå opp ikonet og lagrer filstien serveren svarer med
 * (`/entitysettings/{entity_alias}/tips/{filnavn}`). Eldre maler har i stedet
 * et rent filnavn som ligger blant API-ets innebygde SVG-er — begge må virke.
 *
 * @example tipsIconUrl('/entitysettings/lyfti/tips/pil-a1b2.webp')
 *          // → '<api>/entitysettings/lyfti/tips/pil-a1b2.webp'
 * @example tipsIconUrl('lightbulb.svg')  // → '<api>assets/svg/lightbulb.svg'
 */
export const tipsIconUrl = (icon?: string): string | null => {
    if (!icon) return null;
    if (/^https?:\/\//i.test(icon)) return icon;
    const base = import.meta.env.VITE_API_BASE_URL;
    if (icon.startsWith('/')) return base.replace(/\/$/, '') + icon;
    return base + 'assets/svg/' + icon;
};

// export const getBrregInfo = async (value: string) => {
//     const response = await fetch('https://data.brreg.no/enhetsregisteret/api/enheter/' + value);
//     if (response.status !== 200) {
//         throw {
//             valid: false,
//             error: true,
//             message: 'Fant ikke organisasjon i brønnøysundregisteret'
//         };
//     }

//     const data = await response.json();
//     return data;
// };

export const getBrregInfo = async (value: string) => {
  const orgnr = String(value).trim();

  const endpoints = [
    `https://data.brreg.no/enhetsregisteret/api/enheter/${orgnr}`,
    `https://data.brreg.no/enhetsregisteret/api/underenheter/${orgnr}`,
  ];

  for (const url of endpoints) {
    const response = await fetch(url);

    if (response.status === 200) {
        const data = await response.json();
        const isSubUnit = url.includes("/underenheter/");
        
        let roles = [];
        const rolesOrgnr = isSubUnit ? data.overordnetEnhet : orgnr;
        if (rolesOrgnr) {
            try {
                const roleResponse = await fetch(
                    `https://data.brreg.no/enhetsregisteret/api/enheter/${rolesOrgnr}/roller`
                );
                if (roleResponse.ok) roles = await roleResponse.json();
            } catch {
                // Roller er tilleggsinfo — et feilet oppslag skal ikke stoppe navn/kommune.
            }
        }

        return {
            ...data,
            source: isSubUnit ? "underenhet" : "enhet",
            roles
        };
    }

    // Stop immediately for unexpected server/client errors
    if (response.status !== 404) {
      throw {
        valid: false,
        error: true,
        message: "Feil ved oppslag mot Brønnøysundregisteret",
        status: response.status,
      };
    }
  }

  throw {
    valid: false,
    error: true,
    message: "Fant ikke organisasjon i Brønnøysundregisteret",
  };
};

export const getCityFromPostCode = async (zipcode: string) => {
    const headers = {
        'X-MyBring-API-Uid': 'post@catchmedia.no',
        'X-MyBring-API-Key': 'aec45816-0c00-4ac8-88dc-a6d3e29cd771'
        //'X-Bring-Client-URL': window.location.href
    }

    const url = `https://api.bring.com/pickuppoint/api/postalCode/NO/getCityAndType/${zipcode}.json`;
    const response = await fetch(url, { headers });
    //const response = await fetch(url);
    const json = await response.json();
    if (response.ok) {
        return json.postalCode.city;
        //return json.data.city;
    } else {
        return '';
    }
}

// Serialiserer ett skjemafelt til markdown for KI (Iver) mens søkeren fyller ut.
// Speiler `renderFormElement` (portalApplicationTemplate) — og admin-siden sin
// `getElementValue` (lyfti-admin/src/helpers/utils.js) — slik at ALLE felttyper,
// inkl. kompleks-typene budgetlines/repeater/expensetable/fileupload, sendes som
// lesbar markdown i stedet for [object Object].
//
// `formData` (valgfri) brukes av `radiogroupdepending` for å ta med verdiene til
// de betingede underfeltene (som ikke er egne top-level felt).
//
// Retur: markdown-streng, eller «senere steg»-melding for tomme felt (som Iver
// skal ignorere jf. APPLICANT-instruksene). Aldri `null` → ingen literal "null"
// i payloaden.
export const getElementValue = (
    element: FormElementType,
    value: any,
    formData: Record<string, any> = {}
): string | null => {
    const EMPTY = "*Ikke utfylt, kommer på et senere steg i skjemaet.*";

    const isEmpty =
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
    if (isEmpty) return EMPTY;

    // Kompleks-felt kan ligge som JSON-streng i payload — parse til array.
    const parseRows = (val: any): any[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string" && val) {
            try {
                const p = JSON.parse(val);
                return Array.isArray(p) ? p : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const toNumber = (val: any) => Number(String(val ?? "").replace(/\s+/g, "")) || 0;

    let v: string | null = null;
    switch (element.type) {
        case "dropdown":
            v = element.options?.find((o) => o.value === value)?.label ?? String(value);
            break;

        case "RadioGroup":
        case "bigradio":
            v = element.alternatives?.find((a) => a.value === value)?.label ?? String(value);
            break;

        case "radiogroupdepending": {
            const parts = [
                element.alternatives?.find((a) => a.value === value)?.label ?? String(value),
            ];
            const dep = (element.depends || []).find((d) => d.radiovalue === value);
            for (const inner of dep?.innerElements || []) {
                const iv = formData?.[inner.name];
                if (iv !== undefined && iv !== null && iv !== "") {
                    parts.push(`  - **${inner.label}**: ${getElementValue(inner, iv, formData)}`);
                }
            }
            v = parts.join("\n");
            break;
        }

        case "dropdowncheck": {
            let arr: any = value;
            if (typeof arr === "string") {
                try { arr = JSON.parse(arr); } catch { arr = [value]; }
            }
            if (!Array.isArray(arr)) {
                v = arr?.label ?? String(arr);
                break;
            }
            v = arr
                .map((item: any) => {
                    const label =
                        typeof item === "object" && item !== null
                            ? item.label
                            : element.options?.find((o) => o.value === item)?.label ?? item;
                    return `- ${label}`;
                })
                .join("\n");
            break;
        }

        case "numberformat":
            v = formatNumber(value);
            break;

        case "account": {
            const d = String(value).replace(/\s+/g, "");
            v = d.length === 11 ? `${d.slice(0, 4)} ${d.slice(4, 6)} ${d.slice(6)}` : String(value);
            break;
        }

        case "date": {
            const d = new Date(value);
            v = isNaN(d.getTime())
                ? String(value)
                : `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
            break;
        }

        case "fileuploadsingle":
            v = value?.name ?? value?.label ?? (typeof value === "string" ? value : null);
            break;

        case "fileupload": {
            const files = Array.isArray(value) ? value : value ? [value] : [];
            const names = files
                .map((f: any) => (typeof f === "object" && f !== null ? f.name ?? f.label : f))
                .filter(Boolean);
            v = names.length ? names.map((n: any) => `- ${n}`).join("\n") : null;
            break;
        }

        case "budgetlines": {
            const rows = parseRows(value);
            if (!rows.length) break;
            const cols = (element.columns || []).filter((c) => c.name !== "belop");
            const total = rows.reduce((s, r) => s + toNumber(r.belop), 0);
            const lines = rows.map((r, i) => {
                const cells = cols.length
                    ? cols.map((c) => `${r[c.name] ?? ""}`).filter((x) => x !== "")
                    : Object.entries(r).filter(([k]) => k !== "belop").map(([, val]) => val);
                return `${i + 1}. ${cells.join(" – ")} – ${toNumber(r.belop).toLocaleString("nb-NO")} kr`;
            });
            v = [...lines, `**Totalt: ${total.toLocaleString("nb-NO")} kr**`].join("\n");
            break;
        }

        case "repeater": {
            const rows = parseRows(value);
            if (!rows.length) break;
            const cols = element.columns || [];
            v = rows
                .map((r, i) => {
                    const cells = cols.length
                        ? cols.map((c) => `${c.label}: ${r[c.name] ?? ""}`)
                        : Object.values(r).map((val) => `${val}`);
                    return `${i + 1}. ${cells.join(" – ")}`;
                })
                .join("\n");
            break;
        }

        case "expensetable": {
            const rows = parseRows(value);
            if (!rows.length) break;
            const defaultCols: ExpenseTableColumn[] = [
                { name: "beskrivelse", label: "Beskrivelse", type: "text" },
                { name: "belop", label: "Beløp (kr)", type: "number" },
                { name: "bilag", label: "Bilag", type: "attachment" },
            ];
            const cols = element.columns && element.columns.length ? element.columns : defaultCols;
            const totalCol =
                element.totalColumn && cols.find((c) => c.name === element.totalColumn && c.type === "number")
                    ? element.totalColumn
                    : cols.find((c) => c.type === "number")?.name;
            // Escape av pipe/linjeskift så cellene ikke bryter markdown-tabellen.
            const esc = (s: any) => String(s ?? "").replace(/\|/g, "\\|").replace(/\r?\n/g, " ");
            const cellFor = (c: ExpenseTableColumn, r: any) => {
                if (c.type === "attachment") {
                    const att = r[c.name];
                    const nm = att && typeof att === "object" ? att.name : null;
                    return nm ?? "(intet bilag)";
                }
                if (c.type === "number") return toNumber(r[c.name]).toLocaleString("nb-NO");
                return r[c.name] ?? "";
            };
            const tableLines = [
                `| ${cols.map((c) => esc(c.label)).join(" | ")} |`,
                `| ${cols.map(() => "---").join(" | ")} |`,
                ...rows.map((r) => `| ${cols.map((c) => esc(cellFor(c, r))).join(" | ")} |`),
            ];
            if (totalCol) {
                const total = rows.reduce((s, r) => s + toNumber(r[totalCol]), 0);
                const totalRow = cols.map((c) =>
                    c.name === totalCol ? `**${total.toLocaleString("nb-NO")} kr**` : ""
                );
                // Merk totalraden med "Totalt" i første kolonne når den ikke er tall-kolonnen.
                if (cols[0] && cols[0].name !== totalCol) totalRow[0] = "**Totalt**";
                tableLines.push(`| ${totalRow.join(" | ")} |`);
            }
            v = tableLines.join("\n");
            break;
        }

        default:
            v = typeof value === "object" ? JSON.stringify(value) : String(value);
    }

    if (v === null || v === undefined || v === "") return EMPTY;
    return `${element.prefix ? ` ${element.prefix}` : ""}${v}${element.suffix ? ` ${element.suffix}` : ""}`;
}

// ZOD SCHEMA GENERATION